/**
 * /pipeline-facility/jobs/new — open a job (POST /pipeline-facility/jobs,
 * fieldiq-api#1200). A full page, not a modal. On save the job's own page
 * replaces this one, so Back lands on the register.
 *
 * PF_DISPATCH (admin, office staff, supervisor, dispatcher) only; any other
 * role is told so here rather than handed a form the API would refuse.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useCreatePfJob, usePfCustomers, usePfMeta, usePfUsers } from '../../hooks/usePipelineFacility';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canDispatchPf, pfRefusal } from '../../lib/pipeline-facility';
import { useBackToList } from '../downhole/recordPage';
import { JobForm } from './JobForm';
import { PF_EYEBROW, PfEmpty, PfLoading, PfPanel } from './PfBits';

export default function PfNewJobScreen() {
  const navigate = useNavigate();
  const back = useBackToList('/pipeline-facility/jobs');
  const { user } = useAuth();
  const canAdd = canDispatchPf(user?.role);
  const meta = usePfMeta();
  const customers = usePfCustomers(canAdd);
  const users = usePfUsers(canAdd);
  const create = useCreatePfJob();
  const [error, setError] = useState<string | null>(null);
  const metaFailure = queryLoadFailure(meta);
  const customersFailure = queryLoadFailure(customers);

  return (
    <div data-testid="pf-new-job-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={PF_EYEBROW} title="New job" description="Draft it now, or schedule it to let the crew log work." onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <PfPanel eyebrow="Job register" title="Job">
            {!canAdd ? (
              <PfEmpty>Only an admin, office staff, a supervisor or a dispatcher can open a job.</PfEmpty>
            ) : meta.isLoading || customers.isLoading ? (
              <PfLoading label="Loading form" />
            ) : metaFailure || customersFailure ? (
              <LoadFailureNotice
                error={queryFailureError(metaFailure ? meta : customers)}
                kind={(metaFailure ?? customersFailure) || 'unreachable'}
                subject={metaFailure ? 'the job form' : 'your customers'}
              />
            ) : (customers.data ?? []).length === 0 ? (
              <PfEmpty>A job belongs to a customer, and there are no customers yet. Add one under Clients first.</PfEmpty>
            ) : (
              <JobForm
                job={null}
                catalog={meta.data?.data ?? null}
                meta={meta.data?.meta ?? null}
                customers={customers.data ?? []}
                users={users.data ?? []}
                pending={create.isPending}
                error={error}
                submitLabel="Create job"
                onCancel={back}
                onSubmit={(body) => {
                  setError(null);
                  create.mutate(body, {
                    onSuccess: (res) => {
                      toast.success(res.data?.jobNumber ? `Job ${res.data.jobNumber} created` : 'Job created');
                      navigate(res.data?.id ? `/pipeline-facility/jobs/${res.data.id}` : '/pipeline-facility/jobs', { replace: true });
                    },
                    onError: (err) => setError(pfRefusal(err, 'The job could not be created.')),
                  });
                }}
              />
            )}
          </PfPanel>
        </div>
      </section>
    </div>
  );
}
